<script setup>
import { watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';

import AppFooter from './components/footer/AppFooter.vue';
import AppHeader from './components/header/AppHeader.vue';
import { getCurrentUser } from '@/api/apiClient/auth';
import { useAuthStore } from '@/features/auth/authStore';
import { useConfigStore } from '@/features/config/configStore';
import NotificationToast from '@/features/notifications/NotificationToast.vue';

const authStore = useAuthStore();
const configStore = useConfigStore();
const router = useRouter();
const route = useRoute();

// Runtime instance config is fetched during app bootstrap (main.js) before
// mount, so Sentry can initialize before the app renders.

// Redirect to login after config and auth initialization complete
// if in private instance mode and not authenticated
// Watch route.name to re-evaluate when route resolves
watch(
  [
    () => configStore.configLoaded,
    () => authStore.isInitializing,
    () => route.name,
  ],
  ([loaded, initializing, routeName]) => {
    if (!loaded || initializing) return;

    // Skip if route hasn't resolved yet
    if (routeName == null) return;

    const user = getCurrentUser();
    const publicRoutes = ['login', 'access-denied'];
    const isPublicPage = publicRoutes.includes(String(routeName));

    if (configStore.isPrivateInstance && !user && !isPublicPage) {
      router.push('/login');
    }
  },
  // Evaluate on registration: config is loaded during bootstrap before this
  // component mounts, so the guard cannot rely on observing a post-mount change.
  { immediate: true },
);
</script>

<template>
  <div id="app">
    <template v-if="authStore.isInitializing || !configStore.configLoaded">
      <div data-testid="auth-loading" class="auth-loading">
        <div class="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    </template>
    <template v-else>
      <AppHeader />
      <main>
        <router-view />
      </main>
      <AppFooter />
      <NotificationToast />
    </template>
  </div>
</template>

<style>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--background);
}

main {
  flex: 1;
  position: relative;
}

.auth-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  color: var(--muted-foreground);
  gap: 1rem;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.auth-loading p {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
